import { Directive, HostListener, Inject, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalService } from '../services/modal.service';
import { MessagePopupComponent } from '../components/message-popup/message-popup.component';
import { ScanMessages, popupWidth } from '../utility/constants';
import { DOCUMENT } from '@angular/common';

@Directive({
  selector: '[appScanInput]',
})
export class ScannerInputDirective implements OnDestroy {
  private scanBuffer = '';
  private lastKeyTime = 0;
  private preScanValue = '';
  private scanTimeout: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;
  private afterClosedSubscription?: Subscription;
  private pendingConfirmResolve: ((value: boolean) => void) | null = null;
  private isPopupOpen = false;
  private scanHandled = false;
  private scanInProgress = false;
  private isConfirmationInProgress = false;
  private scanStarted = false;
  private confirmOverwritePromise: Promise<boolean> | null = null;
  private isHandlingScan = false;

  private readonly focusBlockHandler = (e: Event) => {
    // Prevent focusout or blur events while popup is open
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly modalService: ModalService
  ) {}

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.scanTimeout !== undefined) {
      clearTimeout(this.scanTimeout);
      this.scanTimeout = undefined;
    }
    this.afterClosedSubscription?.unsubscribe();
    this.afterClosedSubscription = undefined;
    if (this.pendingConfirmResolve) {
      const resolve = this.pendingConfirmResolve;
      this.pendingConfirmResolve = null;
      this.isConfirmationInProgress = false;
      this.isPopupOpen = false;
      this.confirmOverwritePromise = null;
      this.unblockFocusEvents();
      resolve(false);
    } else {
      this.unblockFocusEvents();
    }
  }

  // Block focusout/blur events when popup is open
  private blockFocusEvents() {
    window.addEventListener('blur', this.focusBlockHandler, true);
    window.addEventListener('focusout', this.focusBlockHandler, true);
  }

  // Unblock focusout/blur events when popup is closed
  private unblockFocusEvents() {
    window.removeEventListener('blur', this.focusBlockHandler, true);
    window.removeEventListener('focusout', this.focusBlockHandler, true);
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    if (this.isPopupOpen) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  @HostListener('document:keydown', ['$event'])
  async handleKeydown(event: KeyboardEvent): Promise<void> {
    const active = this.document.activeElement as HTMLInputElement | null;
    const currentTime = new Date().getTime();
    const isFastScan = currentTime - this.lastKeyTime <= 40;
    if (!isFastScan) {
      this.scanBuffer = '';
    }
    this.lastKeyTime = currentTime;
    const isCharacterKey = event.key.length === 1;
    const isScanInputFocused = this.isScanInput(active);
    const isNonScanField = this.isNonScanField(active);
    if (isCharacterKey && isFastScan && isNonScanField) {
      // Only clear if it looks like a real scan (e.g., 6+ fast characters)
      if (this.scanBuffer.length + 1 >= 6) {
        event.preventDefault();
        setTimeout(() => {
          if (this.destroyed || !active) {
            return;
          }
          active.value = '';
          active.dispatchEvent(new Event('input', { bubbles: true }));
        }, 0);
      }
    }
    if (this.scanBuffer === '') {
      this.preScanValue = active?.value?.trim() ?? '';
      this.scanHandled = false;
    }
    if (isCharacterKey) {
      this.scanBuffer += event.key;
    }
    if (event.key === 'Enter') {
      await this.processScanOnEnter(active, isScanInputFocused);
      return;
    }
    if (this.scanTimeout !== undefined) {
      clearTimeout(this.scanTimeout);
    }
    this.scanTimeout = setTimeout(async () => {
      this.scanTimeout = undefined;
      if (this.destroyed) {
        return;
      }
      await this.processScanOnTimeout();
    }, 100);
  }

  private isScanInput(active: HTMLElement | null): boolean {
    const input = active as HTMLInputElement | null;
    return input?.id === 'scanIncomingBag' && !input.disabled;
  }

  private isNonScanField(active: HTMLElement | null): boolean {
    if (!active) return false;
    const tag = active.tagName;
    const id = active.id;
    return (tag === 'INPUT' || tag === 'TEXTAREA') && id !== 'scanIncomingBag';
  }

  private async processScanOnEnter(
    active: HTMLInputElement | null,
    isScanInputFocused: boolean
  ): Promise<void> {
    if (active && !isScanInputFocused) return;
    if (this.shouldSkipScan()) return;
    this.startScan();
    if (this.isPopupOpen || this.isConfirmationInProgress) return;
    await this.handleScan(this.scanBuffer);
    this.resetScan();
    this.scanBuffer = '';
  }

  private async processScanOnTimeout(): Promise<void> {
    if (this.scanBuffer.length < 6) {
      this.scanBuffer = '';
      return;
    }
    if (this.shouldSkipScan()) return;
    this.startScan();
    if (this.isPopupOpen || this.isConfirmationInProgress) return;
    await this.handleScan(this.scanBuffer);
    this.resetScan();
    this.clearActiveInputUnlessAllowed();
    this.scanBuffer = '';
  }

  private shouldSkipScan(): boolean {
    return (
      this.scanStarted ||
      this.isPopupOpen ||
      this.scanInProgress ||
      this.scanHandled
    );
  }

  private startScan(): void {
    this.scanStarted = true;
    this.scanInProgress = true;
    this.scanHandled = true;
  }

  private resetScan(): void {
    this.scanInProgress = false;
    this.scanStarted = false;
  }

  private async handleScan(barcode: string): Promise<void> {
    if (this.isHandlingScan) {
      return;
    }
    this.isHandlingScan = true;
    const scanInput = this.document.getElementById(
      'scanIncomingBag'
    ) as HTMLInputElement | null;
    if (!scanInput || scanInput.disabled) {
      this.isHandlingScan = false;
      return;
    }
    const fullValue = scanInput.value?.trim() ?? '';
    const barcodeIndex = fullValue.indexOf(barcode);
    const existingValue =
      barcodeIndex > 0 ? fullValue.slice(0, barcodeIndex) : fullValue;
    if (existingValue && existingValue !== barcode) {
      // Immediately reset input value before confirmation
      scanInput.value = existingValue;
      scanInput.dispatchEvent(new Event('input', { bubbles: true }));
      setTimeout(() => {
        if (this.destroyed) {
          return;
        }
        scanInput.focus();
        scanInput.setSelectionRange(existingValue.length, existingValue.length);
      }, 0);
      const confirmed = await this.confirmOverwrite(existingValue, barcode);
      if (!confirmed) {
        this.isHandlingScan = false;
        this.scanStarted = false;
        return;
      }
    }
    scanInput.value = barcode;
    scanInput.dispatchEvent(new Event('input', { bubbles: true }));
    setTimeout(() => {
      if (this.destroyed) {
        return;
      }
      scanInput.focus();
      scanInput.setSelectionRange(barcode.length, barcode.length);
    }, 0);
    this.isHandlingScan = false;
  }

  private clearActiveInputUnlessAllowed() {
    const active = this.document.activeElement as HTMLInputElement | null;
    if (active && active.id !== 'scanIncomingBag') {
      setTimeout(() => {
        if (this.destroyed) {
          return;
        }
        active.value = '';
        active.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  }

  private confirmOverwrite(
    existingValue: string,
    newScannedValue: string
  ): Promise<boolean> {
    if (this.isConfirmationInProgress) {
      return this.confirmOverwritePromise ?? Promise.resolve(false);
    }
    this.isConfirmationInProgress = true;
    this.isPopupOpen = true;
    this.blockFocusEvents();
    this.confirmOverwritePromise = new Promise(resolve => {
      this.pendingConfirmResolve = resolve;
      const dialogRef = this.modalService.openPopup(
        {
          message: ScanMessages.scanConfirmationMessage(
            existingValue,
            newScannedValue
          ),
          isMultiple: false,
          isFooterRequired: true,
        },
        ScanMessages.confirmReplace,
        MessagePopupComponent,
        popupWidth.deletePopup
      );
      this.afterClosedSubscription?.unsubscribe();
      this.afterClosedSubscription = dialogRef.afterClosed().subscribe(
        (result: boolean) => {
          this.afterClosedSubscription = undefined;
          this.pendingConfirmResolve = null;
          this.isConfirmationInProgress = false;
          this.isPopupOpen = false;
          this.confirmOverwritePromise = null;
          this.unblockFocusEvents();
          // Focus the input again after confirmation is closed
          if (result === true) {
            setTimeout(() => {
              if (this.destroyed) {
                return;
              }
              const scanInput = this.document.getElementById(
                'scanIncomingBag'
              ) as HTMLInputElement | null;
              if (scanInput && !scanInput.disabled) {
                scanInput.focus();
                scanInput.setSelectionRange(
                  scanInput.value.length,
                  scanInput.value.length
                );
              }
            }, 0);
          }
          resolve(result === true);
        }
      );
    });
    return this.confirmOverwritePromise;
  }
}
